import { Navigate, Route, Routes, useLocation } from "react-router";
import { useEffect } from "react";
import { DefaultPage, Demo1DarkSidebarPage } from "@/pages/dashboards";
import {
  ProfileActivityPage,
  ProfileBloggerPage,
  CampaignsCardPage,
  CampaignsListPage,
  ProjectColumn2Page,
  ProjectColumn3Page,
  ProfileCompanyPage,
  ProfileCreatorPage,
  ProfileCRMPage,
  ProfileDefaultPage,
  ProfileEmptyPage,
  ProfileFeedsPage,
  ProfileGamerPage,
  ProfileModalPage,
  ProfileNetworkPage,
  ProfileNFTPage,
  ProfilePlainPage,
  ProfileTeamsPage,
  ProfileWorksPage,
} from "@/pages/public-profile";
import {
  AccountActivityPage,
  AccountAllowedIPAddressesPage,
  AccountApiKeysPage,
  AccountAppearancePage,
  AccountBackupAndRecoveryPage,
  AccountBasicPage,
  AccountCompanyProfilePage,
  AccountCurrentSessionsPage,
  AccountDeviceManagementPage,
  AccountEnterprisePage,
  AccountGetStartedPage,
  AccountHistoryPage,
  AccountImportMembersPage,
  AccountIntegrationsPage,
  AccountInviteAFriendPage,
  AccountMembersStarterPage,
  AccountNotificationsPage,
  AccountOverviewPage,
  AccountPermissionsCheckPage,
  AccountPermissionsTogglePage,
  AccountPlansPage,
  AccountPrivacySettingsPage,
  AccountRolesPage,
  AccountSecurityGetStartedPage,
  AccountSecurityLogPage,
  AccountSettingsEnterprisePage,
  AccountSettingsModalPage,
  AccountSettingsPlainPage,
  AccountSettingsSidebarPage,
  AccountTeamInfoPage,
  AccountTeamMembersPage,
  AccountTeamsPage,
  AccountTeamsStarterPage,
  AccountUserProfilePage,
} from "@/pages/account";
import {
  NetworkAppRosterPage,
  NetworkMarketAuthorsPage,
  NetworkAuthorPage,
  NetworkGetStartedPage,
  NetworkMiniCardsPage,
  NetworkNFTPage,
  NetworkSocialPage,
  NetworkUserCardsTeamCrewPage,
  NetworkSaasUsersPage,
  NetworkStoreClientsPage,
  NetworkUserTableTeamCrewPage,
  NetworkVisitorsPage,
} from "@/pages/network";
import { AuthPage } from "@/auth";
import { Demo1Layout } from "@/layouts/demo1";
import { AuthBrandedLayout } from "@/layouts/auth-branded";
import { ErrorsRouting } from "@/errors";
import {
  AuthenticationWelcomeMessagePage,
  AuthenticationAccountDeactivatedPage,
  AuthenticationGetStartedPage,
} from "@/pages/authentication";
import {
  AgentsBillingPage,
  AgentsReportPage,
  AgentsAccountantPage,
  AgentsPhonePage,
  AgentsHome,
  AgentBuilderPage,
} from "@/pages/agents";
import { DashBoard } from "@/pages/agents/dashboard/DashBoard";
import LandingPage from "../pages/landing-page";
import TermsAndConditions from "../pages/landing-page/TermsAndConditions";
import PrivacyPolicy from "../pages/landing-page/PrivacyPolicy";
import PricingPage from "../pages/landing-page/pages/PricingPage";
import ContactPage from "../pages/landing-page/pages/ContactPage";
import PaymentPolicy from "../pages/landing-page/pages/PaymentPolicy";
import { AgentsHistoryPage } from "../pages/agents";
import PropTypes from "prop-types";
import { useAuth, useAuthData } from "../hooks/useAuth";
import ActiveAccount from "@/auth/pages/active/ActiveAccount";
import { VerifiedEmailProtected } from "@/auth/pages/active/VerifiedEmailProtected";
import Payment from "../pages/landing-page/pages/Payment";
import ComplaintHandling from "../pages/landing-page/pages/ComplaintHandling";

const ProtectedRoute = ({ children }) => {
  const { syncAuthFromStorage } = useAuth();
  useEffect(() => {
    syncAuthFromStorage();
  }, [syncAuthFromStorage]);
  const authData = useAuthData();
  const location = useLocation();

  // Nếu chưa đăng nhập, redirect về login với return URL
  if (!authData?.isAuthenticated || !authData?.token) {
    return (
      <Navigate to="/auth/login" state={{ from: location.pathname }} replace />
    );
  }

  return children;
};
ProtectedRoute.propTypes = {
  children: PropTypes.node,
};

/**
 * User đã đăng nhập sẽ bị redirect về agent
 */

const GuestOnlyRoute = ({ children }) => {
  const { syncAuthFromStorage } = useAuth();
  useEffect(() => {
    syncAuthFromStorage();
  }, [syncAuthFromStorage]);
  const authData = useAuthData();
  const location = useLocation();

  // Nếu đã đăng nhập, redirect về dashboard hoặc intended page
  if (authData?.isAuthenticated && authData?.token) {
    // Lấy intended destination từ state hoặc mặc định về /dashboard
    const intendedPath = location.state?.from || "/agent";
    return <Navigate to={intendedPath} replace />;
  }

  return children;
};
GuestOnlyRoute.propTypes = {
  children: PropTypes.node,
};

const AppRoutingSetup = () => {
  return (
    <Routes>
      <Route path="error/*" element={<ErrorsRouting />} />
      {/* Route active account - ai cũng truy cập được */}
      <Route path="active/:token" element={<ActiveAccount />} />
      {/* Auth routes - chỉ cho user chưa đăng nhập */}
      <Route
        path="auth/*"
        element={
          <GuestOnlyRoute>
            <AuthPage />
          </GuestOnlyRoute>
        }
      />

      {/* Landing page - accessible cho tất cả */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/intro" element={<LandingPage />} />
      <Route path="/landing-page" element={<LandingPage />} />
      <Route path="/pricing" element={<PricingPage />} />
      <Route path="/contact" element={<ContactPage />} />
      <Route path="/payment-policy" element={<PaymentPolicy />} />
      <Route path="/payment" element={<Payment />} />
      <Route path="/complaint-handling" element={<ComplaintHandling />} />
      <Route path="/terms-and-conditions" element={<TermsAndConditions />} />
      <Route path="/privacy-policy" element={<PrivacyPolicy />} />

      {/* Tất cả routes sau đây cần authentication */}
      <Route
        path="/*"
        element={
          <ProtectedRoute>
            <Routes>
              <Route element={<Demo1Layout />}>
                {/* Agent routes - Core functionality */}
                <Route path="agent" element={<AgentsHome />} />
                <Route path="billing" element={<AgentsBillingPage />} />
                <Route path="report" element={<AgentsReportPage />} />
                <Route path="accountance" element={<AgentsAccountantPage />} />
                <Route path="phone" element={<AgentsPhonePage />} />
                <Route path="history" element={<AgentsHistoryPage />} />
                <Route path="agents/builder" element={<AgentBuilderPage />} />
                {/* Agent routes - Core functionality */}
                <Route path="agent" element={<AgentsHome />} />
                <Route path="billing" element={<AgentsBillingPage />} />
                <Route path="report" element={<AgentsReportPage />} />
                <Route path="accountance" element={<AgentsAccountantPage />} />
                <Route path="phone" element={<AgentsPhonePage />} />
                <Route path="agent/builder" element={<AgentBuilderPage />} />

                {/* Protected Verified Email route removed from Demo1 layout */}

                {/* Profile routes */}
                <Route path="profile" element={<DefaultPage />} />
                <Route path="dark-sidebar" element={<Demo1DarkSidebarPage />} />

                {/* Public profile routes */}
                <Route
                  path="public-profile/profiles/default"
                  element={<ProfileDefaultPage />}
                />
                <Route
                  path="public-profile/profiles/creator"
                  element={<ProfileCreatorPage />}
                />
                <Route
                  path="public-profile/profiles/company"
                  element={<ProfileCompanyPage />}
                />
                <Route
                  path="public-profile/profiles/nft"
                  element={<ProfileNFTPage />}
                />
                <Route
                  path="public-profile/profiles/blogger"
                  element={<ProfileBloggerPage />}
                />
                <Route
                  path="public-profile/profiles/crm"
                  element={<ProfileCRMPage />}
                />
                <Route
                  path="public-profile/profiles/gamer"
                  element={<ProfileGamerPage />}
                />
                <Route
                  path="public-profile/profiles/feeds"
                  element={<ProfileFeedsPage />}
                />
                <Route
                  path="public-profile/profiles/plain"
                  element={<ProfilePlainPage />}
                />
                <Route
                  path="public-profile/profiles/modal"
                  element={<ProfileModalPage />}
                />
                <Route
                  path="public-profile/projects/3-columns"
                  element={<ProjectColumn3Page />}
                />
                <Route
                  path="public-profile/projects/2-columns"
                  element={<ProjectColumn2Page />}
                />
                <Route
                  path="public-profile/works"
                  element={<ProfileWorksPage />}
                />
                <Route
                  path="public-profile/teams"
                  element={<ProfileTeamsPage />}
                />
                <Route
                  path="public-profile/network"
                  element={<ProfileNetworkPage />}
                />
                <Route
                  path="public-profile/activity"
                  element={<ProfileActivityPage />}
                />
                <Route
                  path="public-profile/campaigns/card"
                  element={<CampaignsCardPage />}
                />
                <Route
                  path="public-profile/campaigns/list"
                  element={<CampaignsListPage />}
                />
                <Route
                  path="public-profile/empty"
                  element={<ProfileEmptyPage />}
                />

                {/* Account home routes */}
                <Route
                  path="account/home/get-started"
                  element={<AccountGetStartedPage />}
                />
                <Route
                  path="account/home/user-profile"
                  element={<AccountUserProfilePage />}
                />
                <Route
                  path="account/home/company-profile"
                  element={<AccountCompanyProfilePage />}
                />
                <Route
                  path="account/home/settings-sidebar"
                  element={<AccountSettingsSidebarPage />}
                />
                <Route
                  path="account/home/settings-enterprise"
                  element={<AccountSettingsEnterprisePage />}
                />
                <Route
                  path="account/home/settings-plain"
                  element={<AccountSettingsPlainPage />}
                />
                <Route
                  path="account/home/settings-modal"
                  element={<AccountSettingsModalPage />}
                />

                {/* Account billing routes */}
                <Route
                  path="account/billing/basic"
                  element={<AccountBasicPage />}
                />
                <Route
                  path="account/billing/enterprise"
                  element={<AccountEnterprisePage />}
                />
                <Route
                  path="account/billing/plans"
                  element={<AccountPlansPage />}
                />
                <Route
                  path="account/billing/history"
                  element={<AccountHistoryPage />}
                />

                {/* Account security routes */}
                <Route
                  path="account/security/get-started"
                  element={<AccountSecurityGetStartedPage />}
                />
                <Route
                  path="account/security/overview"
                  element={<AccountOverviewPage />}
                />
                <Route
                  path="account/security/allowed-ip-addresses"
                  element={<AccountAllowedIPAddressesPage />}
                />
                <Route
                  path="account/security/privacy-settings"
                  element={<AccountPrivacySettingsPage />}
                />
                <Route
                  path="account/security/device-management"
                  element={<AccountDeviceManagementPage />}
                />
                <Route
                  path="account/security/backup-and-recovery"
                  element={<AccountBackupAndRecoveryPage />}
                />
                <Route
                  path="account/security/current-sessions"
                  element={<AccountCurrentSessionsPage />}
                />
                <Route
                  path="account/security/security-log"
                  element={<AccountSecurityLogPage />}
                />

                {/* Account members routes */}
                <Route
                  path="account/members/team-starter"
                  element={<AccountTeamsStarterPage />}
                />
                <Route
                  path="account/members/teams"
                  element={<AccountTeamsPage />}
                />
                <Route
                  path="account/members/team-info"
                  element={<AccountTeamInfoPage />}
                />
                <Route
                  path="account/members/members-starter"
                  element={<AccountMembersStarterPage />}
                />
                <Route
                  path="account/members/team-members"
                  element={<AccountTeamMembersPage />}
                />
                <Route
                  path="account/members/import-members"
                  element={<AccountImportMembersPage />}
                />
                <Route
                  path="account/members/roles"
                  element={<AccountRolesPage />}
                />
                <Route
                  path="account/members/permissions-toggle"
                  element={<AccountPermissionsTogglePage />}
                />
                <Route
                  path="account/members/permissions-check"
                  element={<AccountPermissionsCheckPage />}
                />

                {/* Other account routes */}
                <Route
                  path="account/integrations"
                  element={<AccountIntegrationsPage />}
                />
                <Route
                  path="account/notifications"
                  element={<AccountNotificationsPage />}
                />
                <Route
                  path="account/api-keys"
                  element={<AccountApiKeysPage />}
                />
                <Route
                  path="account/appearance"
                  element={<AccountAppearancePage />}
                />
                <Route
                  path="account/invite-a-friend"
                  element={<AccountInviteAFriendPage />}
                />
                <Route
                  path="account/activity"
                  element={<AccountActivityPage />}
                />

                {/* Network routes */}
                <Route
                  path="network/get-started"
                  element={<NetworkGetStartedPage />}
                />
                <Route
                  path="network/user-cards/mini-cards"
                  element={<NetworkMiniCardsPage />}
                />
                <Route
                  path="network/user-cards/team-crew"
                  element={<NetworkUserCardsTeamCrewPage />}
                />
                <Route
                  path="network/user-cards/author"
                  element={<NetworkAuthorPage />}
                />
                <Route
                  path="network/user-cards/nft"
                  element={<NetworkNFTPage />}
                />
                <Route
                  path="network/user-cards/social"
                  element={<NetworkSocialPage />}
                />
                <Route
                  path="network/user-table/team-crew"
                  element={<NetworkUserTableTeamCrewPage />}
                />
                <Route
                  path="network/user-table/app-roster"
                  element={<NetworkAppRosterPage />}
                />
                <Route
                  path="network/user-table/market-authors"
                  element={<NetworkMarketAuthorsPage />}
                />
                <Route
                  path="network/user-table/saas-users"
                  element={<NetworkSaasUsersPage />}
                />
                <Route
                  path="network/user-table/store-clients"
                  element={<NetworkStoreClientsPage />}
                />
                <Route
                  path="network/user-table/visitors"
                  element={<NetworkVisitorsPage />}
                />

                {/* Authentication flow routes */}
                <Route
                  path="auth/welcome-message"
                  element={<AuthenticationWelcomeMessagePage />}
                />
                <Route
                  path="auth/account-deactivated"
                  element={<AuthenticationAccountDeactivatedPage />}
                />
                <Route
                  path="authentication/get-started"
                  element={<AuthenticationGetStartedPage />}
                />

                {/* Dashboard route */}
                <Route path="dashboard" element={<DashBoard />} />

                {/* Default redirect cho authenticated users */}
                <Route path="" element={<Navigate to="/dashboard" replace />} />
                {/* Catch all route for 404 */}
                <Route
                  path="*"
                  element={<Navigate to="/error/404" replace />}
                />
              </Route>

              {/* Protected section using AuthBrandedLayout */}
              <Route element={<AuthBrandedLayout />}>
                <Route
                  path="/zalo/activated"
                  element={<VerifiedEmailProtected />}
                />
              </Route>
            </Routes>
          </ProtectedRoute>
        }
      />

      {/* Catch all route for non-authenticated users */}
      <Route path="*" element={<Navigate to="/auth/login" replace />} />
    </Routes>
  );
};

export { AppRoutingSetup };
